import { ref, computed, watch } from 'vue'
import { useUserStore } from '@/stores/user'

export interface ContactInfo {
  email: string
  fullName: string
  country: string
  countryCode: string
  phoneNumber: string
  isBangkokResident: boolean
  whatsappNumber: string
  line: string
  instagram: string
  tiktok: string
  facebook: string
}

type DataSource = 'profile' | 'localStorage' | 'new' | null

const STORAGE_KEY = 'achiraya_contact_info'
const DATA_SOURCE_KEY = 'achiraya_contact_source'

// Basic email format check (presence is handled separately)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isValidEmail = (v: string) => EMAIL_RE.test(v.trim())

// Default values
const defaultContactInfo: ContactInfo = {
  email: '',
  fullName: '',
  country: '',
  countryCode: '+66',
  phoneNumber: '',
  isBangkokResident: false,
  whatsappNumber: '',
  line: '',
  instagram: '',
  tiktok: '',
  facebook: ''
}

// Reactive state
const contactInfo = ref<ContactInfo>({ ...defaultContactInfo })
const isLoading = ref(false)
const error = ref<string | null>(null)
const isInitialized = ref(false)
const isWatcherSetup = ref(false)
const dataSource = ref<DataSource>(null)
const hasUnsavedChanges = ref(false)

// Load from localStorage on init
function loadFromStorage(): ContactInfo {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return { ...defaultContactInfo, ...JSON.parse(stored) }
    }
  } catch (e) {
    console.error('Failed to load contact info from storage:', e)
  }
  return { ...defaultContactInfo }
}

// Load data source from localStorage
function loadDataSource(): DataSource {
  try {
    const stored = localStorage.getItem(DATA_SOURCE_KEY)
    if (stored) {
      return stored as DataSource
    }
  } catch (e) {
    console.error('Failed to load data source from storage:', e)
  }
  return null
}

// Save to localStorage
function saveToStorage(info: ContactInfo) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(info))
  } catch (e) {
    console.error('Failed to save contact info to storage:', e)
  }
}

// Save data source to localStorage
function saveDataSource(source: DataSource) {
  try {
    if (source) {
      localStorage.setItem(DATA_SOURCE_KEY, source)
    } else {
      localStorage.removeItem(DATA_SOURCE_KEY)
    }
  } catch (e) {
    console.error('Failed to save data source to storage:', e)
  }
}

// Translation keys for data source labels (to be used with $t)
function getDataSourceKey(source: DataSource): string {
  switch (source) {
    case 'profile':
      return 'booking.step_3.data_source.from_profile'
    case 'localStorage':
      return 'booking.step_3.data_source.from_storage'
    case 'new':
      return 'booking.step_3.data_source.new'
    default:
      return ''
  }
}

// Computed validation
// At least one contact channel (WhatsApp / LINE / Instagram / TikTok / Facebook) is required
const hasSocialMedia = computed(() => {
  return (
    contactInfo.value.whatsappNumber.trim().length > 0 ||
    contactInfo.value.line.trim().length > 0 ||
    contactInfo.value.instagram.trim().length > 0 ||
    contactInfo.value.tiktok.trim().length > 0 ||
    contactInfo.value.facebook.trim().length > 0
  )
})

const isValid = computed(() => {
  return (
    contactInfo.value.fullName.trim().length > 0 &&
    contactInfo.value.country.trim().length > 0 &&
    contactInfo.value.phoneNumber.trim().length >= 9 &&
    isValidEmail(contactInfo.value.email) &&
    hasSocialMedia.value
  )
})

// True when user already has name + phone filled in (e.g. from profile)
const hasExistingData = computed(() => {
  return (
    contactInfo.value.fullName.trim().length > 0 &&
    contactInfo.value.phoneNumber.trim().length > 0
  )
})

// Check if contact info is from user profile
const isFromProfile = computed(() => dataSource.value === 'profile')

// Check if contact info is from localStorage
const isFromLocalStorage = computed(() => dataSource.value === 'localStorage')

// Check if this is new data
const isNewData = computed(() => dataSource.value === 'new')

// Returns i18n keys (resolved with $t in the template), not display strings
const validationErrors = computed(() => {
  const errors: Record<string, string> = {}
  const base = 'booking.step_3.errors'

  if (!contactInfo.value.fullName.trim()) {
    errors.fullName = `${base}.full_name_required`
  }

  if (!contactInfo.value.country.trim()) {
    errors.country = `${base}.country_required`
  }

  if (!contactInfo.value.email.trim()) {
    errors.email = `${base}.email_required`
  } else if (!isValidEmail(contactInfo.value.email)) {
    errors.email = `${base}.email_invalid`
  }

  if (!contactInfo.value.phoneNumber.trim()) {
    errors.phoneNumber = `${base}.phone_required`
  } else if (contactInfo.value.phoneNumber.replace(/\D/g, '').length < 9) {
    errors.phoneNumber = `${base}.phone_min_digits`
  }

  // WhatsApp number validation: 9-15 digits (+ prefix allowed)
  if (contactInfo.value.whatsappNumber.trim()) {
    const digitsOnly = contactInfo.value.whatsappNumber.replace(/\D/g, '')
    if (digitsOnly.length < 9) {
      errors.whatsappNumber = `${base}.whatsapp_min_digits`
    } else if (digitsOnly.length > 15) {
      errors.whatsappNumber = `${base}.whatsapp_max_digits`
    }
  }

  // At least one contact channel (WhatsApp / LINE / Instagram / TikTok / Facebook) is required
  if (!hasSocialMedia.value) {
    errors.socialMedia = `${base}.social_media_required`
  }

  return errors
})

// Format phone number with country code
const formattedPhone = computed(() => {
  const clean = contactInfo.value.phoneNumber.replace(/\D/g, '')
  return `${contactInfo.value.countryCode} ${clean}`
})

// Actions
function updateField<K extends keyof ContactInfo>(
  field: K,
  value: ContactInfo[K]
) {
  contactInfo.value[field] = value
  hasUnsavedChanges.value = true

  // If user modifies data, it becomes "new" if it was from localStorage
  // or stays as "profile" if logged in (we track changes separately)
  if (dataSource.value === 'localStorage') {
    dataSource.value = 'new'
    saveDataSource('new')
  }

  saveToStorage(contactInfo.value)
}

function updateContactInfo(updates: Partial<ContactInfo>) {
  contactInfo.value = { ...contactInfo.value, ...updates }
  hasUnsavedChanges.value = true
  saveToStorage(contactInfo.value)
}

function resetContactInfo() {
  contactInfo.value = { ...defaultContactInfo }
  dataSource.value = 'new'
  hasUnsavedChanges.value = false
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(DATA_SOURCE_KEY)
}

// Mark changes as saved (after successful API call)
function markChangesSaved() {
  hasUnsavedChanges.value = false
}

// Load from user profile (after login/fetch)
async function loadFromUserProfile(userData: Partial<ContactInfo>) {
  contactInfo.value = {
    ...contactInfo.value,
    ...userData
  }
  dataSource.value = 'profile'
  saveDataSource('profile')
  saveToStorage(contactInfo.value)
}

export function useContactInfo() {
  const userStore = useUserStore()

  // Initialize contact info from localStorage (safe to do here as it doesn't use Pinia)
  if (!isInitialized.value) {
    // First load from localStorage
    const stored = loadFromStorage()
    const hasStoredData = stored.fullName || stored.phoneNumber

    if (hasStoredData) {
      contactInfo.value = stored
      dataSource.value = loadDataSource() || 'localStorage'
    } else {
      contactInfo.value = { ...defaultContactInfo }
      dataSource.value = 'new'
    }

    saveDataSource(dataSource.value)
    isInitialized.value = true
  }

  // Initialize from user store if logged in
  function initFromUserStore() {
    // Check if user is logged in with profile data
    if (userStore.isLoggedIn && userStore.contactInfo?.fullName) {
      // Only overwrite if we haven't loaded from profile yet
      if (dataSource.value !== 'profile') {
        contactInfo.value = { ...userStore.contactInfo }
        dataSource.value = 'profile'
        saveDataSource('profile')
        saveToStorage(contactInfo.value)
      } else if (!contactInfo.value.email && userStore.contactInfo.email) {
        // Backfill email that may be missing from older localStorage data
        contactInfo.value.email = userStore.contactInfo.email
        saveToStorage(contactInfo.value)
      }
    }

    // Watch for user data changes — guard ensures only one watcher is created across remounts
    if (!isWatcherSetup.value) {
      isWatcherSetup.value = true
      watch(() => userStore.contactInfo, (userContact) => {
        if (userContact && userContact.fullName) {
          // If user has profile data, use it and set source to profile
          const hasProfileData = userContact.fullName || userContact.phoneNumber
          if (hasProfileData && dataSource.value !== 'profile') {
            contactInfo.value = {
              ...contactInfo.value,
              ...userContact
            }
            dataSource.value = 'profile'
            saveDataSource('profile')
            saveToStorage(contactInfo.value)
          } else if (hasProfileData && !contactInfo.value.email && userContact.email) {
            // Backfill email missing from older localStorage data (auth may load async)
            contactInfo.value.email = userContact.email
            saveToStorage(contactInfo.value)
          }
        }
      }, { immediate: true })
    }
  }

  // Save contact info to user profile
  async function saveToProfile(): Promise<boolean> {
    if (!userStore.isLoggedIn) {
      error.value = 'login_required'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch('/api/v1/user/update_from_checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contact_info: {
            full_name: contactInfo.value.fullName,
            country: contactInfo.value.country,
            country_code: contactInfo.value.countryCode,
            phone_number: contactInfo.value.phoneNumber,
            is_bangkok_resident: contactInfo.value.isBangkokResident,
            whatsapp_number: contactInfo.value.whatsappNumber,
            social_media: {
              line: contactInfo.value.line,
              instagram: contactInfo.value.instagram,
              tiktok: contactInfo.value.tiktok,
              facebook: contactInfo.value.facebook
            }
          }
        })
      })

      if (response.ok) {
        dataSource.value = 'profile'
        saveDataSource('profile')
        markChangesSaved()

        // Update user store
        await userStore.fetchCurrentUser()

        return true
      } else {
        const data = await response.json()
        error.value = 'save_failed'
        return false
      }
    } catch (e) {
      error.value = 'network_error'
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    contactInfo,
    isLoading,
    error,
    dataSource,
    hasUnsavedChanges,

    // Computed
    isValid,
    validationErrors,
    formattedPhone,
    hasExistingData,
    isFromProfile,
    isFromLocalStorage,
    isNewData,

    // Getters - return translation keys instead of hardcoded text
    dataSourceKey: computed(() => getDataSourceKey(dataSource.value)),

    // Actions
    updateField,
    updateContactInfo,
    resetContactInfo,
    loadFromUserProfile,
    saveToProfile,
    markChangesSaved,
    initFromUserStore
  }
}
